/**
 * In-flight request coalescer for the account-self reads the Lobby
 * fires on mount.
 *
 * React 18 StrictMode double-invokes effects in development, and a
 * fast remount (re-login, reconnect, WS displaced -> re-auth) can
 * re-arm the same mount effect before the first request settles. The
 * per-request signed nonce already guarantees two such requests sign
 * DISTINCT bytes (so neither false-trips the server replay cache),
 * but firing two identical reads at once still doubles the network
 * traffic and races a banner-state setter. This coalescer collapses
 * concurrent calls that share a logical key into a SINGLE underlying
 * request: the first call invokes the factory and caches the pending
 * promise under the key; every concurrent caller awaits that same
 * promise; once it settles (resolve OR reject) the key is released so
 * the NEXT logical fetch re-signs and re-fetches fresh.
 *
 * This is deliberately a coalescer, not a cache: it never serves a
 * stale result to a request that starts AFTER the prior one
 * completed. A manual retry tap or a later mount always gets a fresh,
 * freshly-signed round-trip.
 */

const inFlight = new Map<string, Promise<unknown>>();

/**
 * Run `factory` under an in-flight guard keyed on `key`. While a
 * call for `key` is pending, concurrent callers share its promise
 * instead of invoking `factory` again. The entry is cleared as soon
 * as the promise settles (success or failure) so a subsequent call
 * starts a fresh request.
 */
export function dedupeInFlight<T>(
  key: string,
  factory: () => Promise<T>,
): Promise<T> {
  const existing = inFlight.get(key);
  if (existing !== undefined) {
    return existing as Promise<T>;
  }
  const pending = factory().finally(() => {
    // Release only if WE are still the owning entry — a settled
    // promise that was already replaced (cannot happen while one
    // entry is pending, but defensive against future reentrancy)
    // must not evict a newer in-flight request.
    if (inFlight.get(key) === pending) {
      inFlight.delete(key);
    }
  });
  inFlight.set(key, pending);
  return pending;
}

/** Test-only — drop every tracked in-flight entry so a unit test
 *  starts from a clean coalescer. Never called by production code. */
export function resetInFlightDedupeForTest(): void {
  inFlight.clear();
}
